import { Link } from "react-router-dom";
import { Shield, FileCheck, Lock, ChevronRight, CheckCircle, ArrowRight, Building, Cpu, Heart, Factory, Landmark, Zap } from "lucide-react";
import { useLang } from "@/context/LangContext";

const HERO_BG = "https://images.unsplash.com/photo-1761706885595-02fdd9fe91bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMGFyY2hpdGVjdHVyZSUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc1NDY0MjcyfDA&ixlib=rb-4.1.0&q=85";
const TEAM_IMG = "https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBwcm9mZXNzaW9uYWwlMjB0ZWFtJTIwbWVldGluZyUyMGJvYXJkcm9vbXxlbnwwfHx8fDE3NzU0NjUzMTN8MA&ixlib=rb-4.1.0&q=85";
const ISO_LOGO = "https://customer-assets.emergentagent.com/job_secure-certified/artifacts/ha193hnt_iso.png";
const IAF_LOGO = "https://customer-assets.emergentagent.com/job_secure-certified/artifacts/18doz6dk_iad.svg.png";
const ICON_SIZE_SM = 22;

const standardCards = [
  { code: "ISO 27001", name: "Information Security Management", color: "#13A8BD" },
  { code: "ISO 9001", name: "Quality Management Systems", color: "#0B2545" },
  { code: "ISO 22301", name: "Business Continuity Management", color: "#13A8BD" },
  { code: "ISO 27701", name: "Privacy Information Management", color: "#0B2545" },
  { code: "SOC 2", name: "Trust Services Criteria", color: "#13A8BD" },
  { code: "IAF", name: "International Accreditation Framework Aligned", color: "#0B2545" },
];

const serviceIcons = [Shield, FileCheck, Shield, Lock, FileCheck, Lock, Shield];
const serviceKeys = [
  { title: "ISO/IEC 27001 Certification", desc: "ISMS certification through Stage 1 and Stage 2 audits with surveillance program." },
  { title: "ISO 9001 Certification", desc: "Quality management system certification for international standards recognition." },
  { title: "ISO 22301 Certification", desc: "Business continuity management ensuring organizational resilience." },
  { title: "ISO 27701 Certification", desc: "Privacy information management extending ISO 27001 coverage." },
  { title: "SOC 2 Assessment", desc: "Trust Services Criteria readiness and assessment for Type I and Type II reports." },
  { title: "Compliance Assessments", desc: "Gap analysis, readiness audits, and pre-certification reviews." },
  { title: "Cybersecurity Assurance", desc: "Vulnerability assessment, penetration testing, and cloud security." },
];

const industryIcons = [Building, Heart, Cpu, Landmark, Zap, Factory];

const insights = [
  { date: "15 Dec 2025", category: "Standards", title: "ISO 27001:2022 Transition — What Organizations Need to Know", desc: "The transition deadline is approaching. Current 2013-certificates must be transitioned." },
  { date: "08 Dec 2025", category: "Cybersecurity", title: "AI-Enabled Cyber Attacks: Preparing Your ISMS", desc: "Organizations must update their ISMS to account for AI-specific threats." },
  { date: "01 Dec 2025", category: "Compliance", title: "SOC 2 vs ISO 27001: Choosing the Right Framework", desc: "A comparison to help organizations choose the appropriate compliance framework." },
];

export default function HomePage() {
  const { t } = useLang();
  const s = t?.sections || {};
  const trustItems = t?.trust || ["Independent & Impartial", "Accredited Partner Network", "Certified Lead Auditors", "Global Standards Alignment"];
  const processStepNames = t?.process_steps || ["Application", "Gap Analysis", "Stage 1 Audit", "Stage 2 Audit", "Certification"];
  const industryNames = t?.industries_names || ["Financial Services", "Healthcare", "Technology", "Government", "Energy", "Manufacturing"];
  const statLabels = t?.stats || {};

  const stats = [
    { value: "2-in-1", label: statLabels.combined || "Security + Certification Combined" },
    { value: "90d", label: statLabels.time || "Average Time to Certification Readiness" },
    { value: "IAF", label: statLabels.accredited || "Accredited Partner Certifications" },
    { value: "24/7", label: statLabels.monitoring || "Continuous Security Monitoring" },
  ];

  return (
    <div className="page-transition" data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/85 to-[#0B2545]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 lg:py-40 w-full">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#13A8BD] border border-[#13A8BD]/30 px-4 py-2">
                {t?.hero?.badge || "International Certification Board"}
              </span>
            </div>
            <h1 data-testid="hero-heading" className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] tracking-tighter font-black text-white leading-[1.08] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
              {t?.hero?.title || "Trusted Certification & Cybersecurity Assurance"}
            </h1>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 mb-4">
              {t?.hero?.subtitle_pre || "Internationally Recognized Standards"}
            </p>
            <p data-testid="hero-subtitle" className="text-base md:text-lg text-white/60 leading-relaxed mb-10 max-w-2xl">
              {t?.hero?.subtitle || "GACA International is an independent certification board delivering ISO certification, compliance audits, and cybersecurity assurance services worldwide through our accredited partner network."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-button" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
                {t?.hero?.cta_primary || "Apply for Certification"} <ArrowRight size={16} />
              </Link>
              <Link to="/services" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 border-2 border-white/25 text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-white hover:text-[#0B2545] transition-all duration-200">
                {t?.hero?.cta_secondary || "Our Standards"}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-5 h-8 border-2 border-white/25 rounded-full flex justify-center pt-1">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section data-testid="trust-bar" className="bg-[#F7F9FC] border-b border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustItems.map((item) => (
              <div key={item} data-testid={`trust-item-${item.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-center gap-3">
                <CheckCircle size={16} className="text-[#13A8BD] flex-shrink-0" />
                <span className="text-xs font-medium text-[#0B2545]/80 uppercase tracking-wider">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards Cards */}
      <section data-testid="standards-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-8 mb-14">
            <img src={ISO_LOGO} alt="ISO" className="h-12 w-auto opacity-70" />
            <img src={IAF_LOGO} alt="IAF" className="h-12 w-auto opacity-70" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-1">{s.standards_label || "Standards We Certify"}</p>
              <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>
                {s.standards_title || "Internationally Recognized Certifications"}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[#0B2545]/10 border border-[#0B2545]/10">
            {standardCards.map((card, i) => (
              <div key={card.code} data-testid={`standard-card-${i}`} className="p-8 lg:p-10 bg-white hover:bg-[#F7F9FC] transition-colors text-center">
                <div className="text-3xl lg:text-4xl font-black mb-2" style={{ fontFamily: "Chivo, sans-serif", color: card.color }}>{card.code}</div>
                <p className="text-xs text-slate-500">{card.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-20 lg:py-28 bg-[#F7F9FC] border-y border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{s.services_label || "Certification Services"}</p>
              <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{s.services_title || "What We Certify"}</h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-sm text-slate-600 leading-relaxed">
                {s.services_desc || "As an independent certification board, GACA delivers audit and certification services through our network of accredited partner bodies across all major management system standards."}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0B2545]/10 border border-[#0B2545]/10">
            {serviceKeys.map((service, i) => {
              const Icon = serviceIcons[i];
              return (
                <Link to="/services" key={service.title} data-testid={`service-card-${i}`} className="service-card p-8 lg:p-10 bg-white hover:bg-[#F7F9FC] transition-all duration-300 group">
                  <Icon size={24} className="text-[#13A8BD] mb-4" strokeWidth={1.5} />
                  <h3 className="text-sm font-bold text-[#0B2545] mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>{service.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-3">{service.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#13A8BD] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                    {s.learn_more || "Learn More"} <ChevronRight size={12} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section data-testid="process-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{s.process_label || "Certification Process"}</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{s.process_title || "Your Path to Certification"}</h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-[2px] bg-[#13A8BD]/15" />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {processStepNames.map((step, i) => (
                <div key={step} data-testid={`process-step-${i}`} className="relative text-center">
                  <div className="relative z-10 inline-flex items-center justify-center w-16 h-16 bg-white border-2 border-[#13A8BD] text-[#13A8BD] mb-3 shadow-sm">
                    <span className="text-xl font-black" style={{ fontFamily: "Chivo, sans-serif" }}>{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-xs font-bold text-[#0B2545] uppercase tracking-wider">{step}</h3>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-10 text-center">
            <Link to="/process" data-testid="process-learn-more" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2545] uppercase tracking-wider hover:text-[#13A8BD] transition-colors">
              {s.process_full || "Full Certification Process"} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* About + Stats */}
      <section className="py-20 lg:py-28 bg-[#0B2545]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{s.advantage_label || "The GACA Advantage"}</p>
              <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-white mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
                {s.advantage_title || "We Don't Just Certify — We Secure"}
              </h2>
              <p className="text-sm text-white/55 leading-relaxed mb-8">
                {s.advantage_desc || "GACA International combines deep cybersecurity expertise with internationally aligned certification processes. Your organization receives not just a certificate, but a thorough security evaluation through our accredited partner network."}
              </p>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {stats.map((stat, i) => (
                  <div key={stat.value} data-testid={`stat-${i}`} className="border border-white/10 p-4">
                    <div className="text-xl font-black text-[#13A8BD]" style={{ fontFamily: "Chivo, sans-serif" }}>{stat.value}</div>
                    <p className="text-[10px] text-white/45 mt-1 uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>
              <Link to="/why-gaca" className="inline-flex items-center gap-2 text-sm font-semibold text-[#13A8BD] uppercase tracking-wider hover:text-white transition-colors">
                {s.why_choose || "Why Choose GACA"} <ArrowRight size={16} />
              </Link>
            </div>
            <div>
              <img src={TEAM_IMG} alt="GACA team" className="w-full h-96 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section data-testid="industries-section" className="py-16 bg-[#F7F9FC] border-y border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-2">{s.industries_label || "Industries"}</p>
              <h2 className="text-2xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{s.industries_title || "Sectors We Serve"}</h2>
            </div>
            <Link to="/industries" className="hidden md:inline-flex items-center gap-2 text-xs font-semibold text-[#0B2545] uppercase tracking-wider hover:text-[#13A8BD] transition-colors">
              {s.all_industries || "All Industries"} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-[#0B2545]/10 border border-[#0B2545]/10">
            {industryNames.map((name, i) => {
              const Icon = industryIcons[i];
              return (
                <Link to="/industries" key={name} data-testid={`industry-${name.toLowerCase().replace(/\s+/g, '-')}`} className="p-5 bg-white hover:bg-[#F7F9FC] transition-colors text-center group">
                  <Icon size={ICON_SIZE_SM} className="mx-auto text-[#13A8BD] mb-2" strokeWidth={1.5} />
                  <h4 className="text-[11px] font-bold text-[#0B2545]">{name}</h4>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insights */}
      <section data-testid="insights-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-3">{s.insights_label || "Latest Insights"}</p>
              <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{s.insights_title || "Our Thinking"}</h2>
            </div>
            <Link to="/insights" data-testid="insights-view-all" className="hidden md:inline-flex items-center gap-2 text-xs font-semibold text-[#0B2545] uppercase tracking-wider hover:text-[#13A8BD] transition-colors">
              {s.view_all || "View All"} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0B2545]/10 border border-[#0B2545]/10">
            {insights.map((item) => (
              <Link to="/insights" key={item.title} data-testid={`insight-${item.title.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`} className="p-8 bg-white hover:bg-[#F7F9FC] transition-colors group">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] text-slate-400">{item.date}</span>
                  <span className="text-[10px] font-bold text-[#13A8BD] uppercase tracking-wider">{item.category}</span>
                </div>
                <h3 className="text-base font-bold text-[#0B2545] mb-2 leading-snug group-hover:text-[#13A8BD] transition-colors" style={{ fontFamily: "Chivo, sans-serif" }}>{item.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-20 lg:py-28 bg-[#F7F9FC] border-t border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            {s.cta_title || "Ready to Get Certified?"}
          </h2>
          <p className="text-sm text-slate-600 max-w-xl mx-auto mb-10">
            {s.cta_subtitle || "Submit your certification application today. Our team of certified lead auditors is ready to guide you through every step of the process."}
          </p>
          <Link to="/contact" data-testid="cta-request-cert" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            {s.cta_button || "Apply for Certification"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
